import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import BASE_URL from "../../configs/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const gradientMaskStyle = {
  WebkitMaskImage: `
    linear-gradient(to bottom, rgba(0,0,0,1) 10%, rgba(0,0,0,10) 100%),
  `,
  WebkitMaskComposite: "intersect",
  maskImage: `
    linear-gradient(to bottom, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 100%),
    linear-gradient(to right, rgba(0,0,0,1) 80%, rgba(0,0,0,0) 100%),
    linear-gradient(to left, rgba(0,0,0,1) 80%, rgba(0,0,0,0) 100%)
  `,
  maskComposite: "intersect",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
};

function HeroSection({ movies }) {
  const [currentMovie, setCurrentMovie] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [favoriteMovies, setFavoriteMovies] = useState([]);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);

  const navigate = useNavigate();

  // Effect to set initial movie and loading state
  useEffect(() => {
    if (movies && movies.length > 0) {
      setCurrentMovie(movies[0]); // Set first movie as default
      setCurrentIndex(0);
      setIsLoading(false);
    } else if (movies && movies.length === 0) {
      setIsLoading(false);
    }
  }, [movies]);
  useEffect(() => {
    if (movies && movies.length > 0) {
      setCurrentMovie(movies[currentIndex]);
    }
  }, [currentIndex, movies]);
  // Auto-scroll effect
  useEffect(() => {
    if (!movies || movies.length <= 1 || isAutoScrollPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % movies.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [movies, isAutoScrollPaused]);

  // Effect 1: Check initial login state
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  const fetchFavoriteMovies = useCallback(async () => {
    if (isLoggedIn) {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setIsLoggedIn(false);
        setFavoriteMovies([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/yeuthich/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFavoriteMovies(res.data || []);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          localStorage.removeItem("accessToken");
          setIsLoggedIn(false);
          setFavoriteMovies([]);
        } else {
          console.error("Error fetching favorite movies:", err);
          setFavoriteMovies([]);
        }
      }
    } else {
      setFavoriteMovies([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchFavoriteMovies();
  }, [fetchFavoriteMovies]);

  useEffect(() => {
    if (isLoggedIn && Array.isArray(favoriteMovies) && currentMovie?.slug) {
      const favorited = favoriteMovies.some(
        (fav) => fav.phim && fav.phim.slug === currentMovie.slug
      );
      setIsFavorited(favorited);
    } else {
      setIsFavorited(false);
    }
  }, [favoriteMovies, currentMovie?.slug, isLoggedIn]);

  const handleToggleFavorite = async () => {
    if (!isLoggedIn) {
      toast.warn("Vui lòng đăng nhập để sử dụng chức năng này.");
      return;
    }
    if (!currentMovie || !currentMovie.slug) return;

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setIsLoggedIn(false);
      return;
    }

    const originallyFavorited = isFavorited;
    setIsFavorited(!originallyFavorited);

    try {
      if (originallyFavorited) {
        await axios.post(`${BASE_URL}/api/yeuthich/remove-phim/`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { phim_slug: currentMovie.slug },
        });
        toast.success(`Đã xóa "${currentMovie.ten_phim}" khỏi yêu thích.`);
        setFavoriteMovies((prevFavs) =>
          prevFavs.filter((fav) => fav.phim?.slug !== currentMovie.slug)
        );
      } else {
        const response = await axios.post(
          `${BASE_URL}/api/yeuthich/add-phim/`,
          { phim_slug: currentMovie.slug },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Đã thêm "${currentMovie.ten_phim}" vào yêu thích.`);
        if (response.data && response.data.phim) {
          setFavoriteMovies((prevFavs) => [...prevFavs, response.data]);
        } else {
          setFavoriteMovies((prevFavs) => [
            ...prevFavs,
            { phim: { ...currentMovie } },
          ]);
        }
      }
    } catch (err) {
      setIsFavorited(originallyFavorited);
      toast.error(
        `Lỗi khi ${originallyFavorited ? "xóa" : "thêm"} phim "${
          currentMovie.ten_phim
        }".`
      );

      if (err.response && err.response.status === 401) {
        localStorage.removeItem("accessToken");
        setIsLoggedIn(false);
        setFavoriteMovies([]);
      }
    }
  };

  const handleThumbnailClick = (movie, index) => {
    setCurrentMovie(movie);
    setCurrentIndex(index);
    setIsAutoScrollPaused(true);
    setTimeout(() => setIsAutoScrollPaused(false), 5000);
  };

  const handleWatchNow = async () => {
    if (!currentMovie || !currentMovie.slug) {
      toast.info("Vui lòng chọn một phim để xem.");
      return;
    }
    try {
      let episodeData = null;
      let languageUsed = "vietsub";

      try {
        const res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${currentMovie.slug}/tap/1/vietsub/`
        );
        episodeData = res.data;
      } catch (error) {
        console.warn("Vietsub not found, trying Thuyet Minh.", error.message);
        languageUsed = "thuyetminh";
        try {
          const resThuyetMinh = await axios.get(
            `${BASE_URL}/api/video-nguon/phim/${currentMovie.slug}/tap/1/thuyetminh/`
          );
          episodeData = resThuyetMinh.data;
        } catch (thuyetMinhError) {
          toast.error("Không tìm thấy nguồn video cho phim này.");

          return;
        }
      }

      navigate(
        `/xem-phim/${currentMovie.slug}/tap/${episodeData.so_tap}?id=${episodeData.id}&ngonngu=${languageUsed}`
      );
    } catch (error) {
      toast.error("Đã có lỗi xảy ra khi chuẩn bị xem phim.");
    }
  };

  if (isLoading) {
    return (
      <section
        className="relative flex flex-col justify-end pt-16 bg-black min-h-[60vh] md:min-h-[80vh]"
        aria-label="Đang tải phim nổi bật"
      >
        <div className="absolute inset-0 z-0 overflow-hidden">
          <Skeleton
            height="100%"
            width="100%"
            baseColor="#202020"
            highlightColor="#444"
          />
        </div>
        <div className="relative z-10 w-full pb-12 md:pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8">
          <div className="lg:pl-10 grid grid-cols-1 lg:grid-cols-12 items-end gap-8">
            <div className="flex flex-col items-center text-center sm:text-left sm:items-start lg:col-span-7 space-y-3 md:space-y-4">
              <Skeleton
                height={40}
                width="80%"
                baseColor="#202020"
                highlightColor="#444"
              />
              <div className="flex flex-wrap gap-3">
                {[...Array(4)].map((_, i) => (
                  <Skeleton
                    key={i}
                    height={24}
                    width={60}
                    baseColor="#202020"
                    highlightColor="#444"
                  />
                ))}
              </div>
              <Skeleton
                height={20}
                width="90%"
                count={2}
                style={{ marginBottom: "0.5rem" }}
                baseColor="#202020"
                highlightColor="#444"
              />
              <div className="hidden sm:flex items-center space-x-3 pt-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    circle={true}
                    height={48}
                    width={48}
                    baseColor="#202020"
                    highlightColor="#444"
                  />
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 flex justify-center items-center sm:items-end">
              <div className="flex space-x-2 py-5">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    height={40}
                    width={60}
                    style={{ borderRadius: "8px" }}
                    baseColor="#202020"
                    highlightColor="#444"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!movies || movies.length === 0) {
    return (
      <section
        className="flex items-center justify-center h-[70vh] text-white bg-[#191B24]"
        aria-label="Không có phim nổi bật"
      >
        <p className="text-xl">Không có phim nào nổi bật để hiển thị.</p>
      </section>
    );
  }

  return (
    <section
      className="p-0 md:p-6 xl:container xl:mx-auto relative flex flex-col justify-end bg-[#191B24] h-[35vh] md:min-h-[80vh]"
      onMouseEnter={() => setIsAutoScrollPaused(true)}
      onMouseLeave={() => setIsAutoScrollPaused(false)}
    >
      {/* Hero Background Image */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {currentMovie && (
            <>
              <motion.img
                key={currentMovie.id || currentMovie.slug}
                src={currentMovie.banner_url || "/placeholder-banner.jpg"}
                alt={`Poster phim ${currentMovie.ten_phim} - ${
                  currentMovie.ten_khac || ""
                }`}
                loading="lazy"
                className="h-full w-full object-cover"
                style={gradientMaskStyle}
                initial={{ opacity: 0, filter: "blur(8px)", scale: 1.05 }}
                animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                exit={{ opacity: 0, filter: "blur(8px)", scale: 1.05 }}
                transition={{ duration: 0.5 }}
              />
              <div
                className="hidden md:block absolute inset-0 z-[1] pointer-events-none"
                style={{
                  backgroundImage: `radial-gradient(rgba(0,0,0,0.1) 0.5px, transparent 0.5px)`,
                  backgroundSize: "3px 3px",
                }}
                aria-hidden="true"
              ></div>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 w-full pb-6 md:pt-32 md:pb-16 lg:pt-40 lg:pb-20 px-4 sm:px-6 lg:px-10 xl:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-end md:gap-8">
          {/* Movie Information */}
          <article className="flex flex-col items-center text-center md:items-start md:text-left lg:col-span-7 space-y-2 md:space-y-4">
            {/* Movie Titles */}
            <motion.header
              key={`title-${currentMovie.id}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h1 className="text-xl sm:text-3xl lg:text-4xl leading-tight lg:leading-snug text-white tracking-wider uppercase font-tieude line-clamp-2">
                {currentMovie.ten_phim || "Tên phim đang cập nhật"}
              </h1>
              <h2 className="text-xs sm:text-lg text-amber-400 tracking-wider line-clamp-2">
                {currentMovie.ten_khac || "Tên phim đang cập nhật"}
              </h2>
            </motion.header>

            {/* Movie Metadata */}
            <motion.div
              key={`meta-${currentMovie.id}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3"
              role="list"
              aria-label="Thông tin phim"
            >
              <span
                className="px-1 py-0.5 rounded-md text-[10px] leading-4  lg:text-xs font-bold text-white tracking-wide shadow-lg border border-orange-500 bg-transparent"
                role="listitem"
                aria-label={`Chất lượng: ${currentMovie.chat_luong}`}
              >
                {currentMovie.chat_luong}
              </span>
              <span
                className="px-1.5 py-0.5 rounded-md text-[10px] leading-4 lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-black bg-white border border-white"
                role="listitem"
                aria-label={`Tình trạng: ${currentMovie.tinh_trang}`}
              >
                {currentMovie.tinh_trang}
              </span>
              <span
                className="px-1.5 py-0.5 rounded-md text-[10px] leading-4 lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent"
                role="listitem"
                aria-label={`Năm phát hành: ${currentMovie.nam_phat_hanh}`}
              >
                {currentMovie.nam_phat_hanh}
              </span>
              <span
                className="px-1.5 py-0.5 rounded-md text-[10px] leading-4 lg:text-xs font-semibold shadow-md hover:shadow-lg transition-shadow text-white border border-white bg-transparent"
                role="listitem"
                aria-label={`Thời lượng: ${currentMovie.thoi_luong}`}
              >
                {currentMovie.thoi_luong}
              </span>
            </motion.div>

            {/* Genres */}
            {currentMovie.the_loai && currentMovie.the_loai.length > 0 && (
              <motion.nav
                key={`genres-${currentMovie.id}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:flex flex-wrap items-center justify-center md:justify-start gap-2 text-xs font-medium"
                aria-label="Thể loại phim"
              >
                {currentMovie.the_loai.slice(0, 3).map((genre) => (
                  <Link
                    key={genre.slug}
                    to={`/the-loai/${genre.slug}`}
                    state={{ ten: genre.ten }}
                    className="bg-orange-200/40 hover:bg-orange-300/60 backdrop-blur-sm text-white px-1.5 py-1 rounded-md transition duration-150"
                    aria-label={`Xem phim thể loại ${genre.ten}`}
                  >
                    {genre.ten}
                  </Link>
                ))}
              </motion.nav>
            )}

            {/* Movie Description */}
            {currentMovie.mo_ta && (
              <motion.div
                key={`desc-${currentMovie.id}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="hidden md:block text-white text-sm max-w-xl lg:max-w-2xl leading-relaxed pt-2"
              >
                <div
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)",
                  }}
                  dangerouslySetInnerHTML={{ __html: currentMovie.mo_ta }}
                />
              </motion.div>
            )}

            {/* Action Buttons */}
            <motion.div
              key={`actions-${currentMovie.id}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="hidden md:flex items-center space-x-3 sm:space-x-4 pt-0 md:pt-5"
              role="group"
              aria-label="Hành động với phim"
            >
              <div className="relative flex items-center justify-center">
                {/* Sóng tròn liên tục và mềm mại */}
                <span className="soft-ping" />
                <span className="soft-ping" />
                <span className="soft-ping" />

                {/* Nút chính */}
                <button
                  onClick={handleWatchNow}
                  style={{
                    background:
                      "linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff4081 100%)",
                  }}
                  className="relative z-10 w-8 h-8 sm:w-14 sm:h-14 flex items-center justify-center rounded-full shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 ease-out"
                  aria-label={`Xem phim ${currentMovie.ten_phim} ngay`}
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 lg:h-7 lg:w-7 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.737 4 21.277 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648z" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center rounded-full px-1.5 lg:py-0 border border-white/20 bg-black/30 backdrop-blur-sm">
                <button
                  onClick={handleToggleFavorite}
                  className="text-white w-8 h-6 sm:w-11 sm:h-11 flex items-center justify-center border-r border-white/30 hover:text-amber-400 transition-colors duration-200"
                  aria-label={
                    isFavorited
                      ? `Xóa phim ${currentMovie.ten_phim} khỏi yêu thích`
                      : `Thêm phim ${currentMovie.ten_phim} vào yêu thích`
                  }
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    className={`transition-all duration-200 ${
                      isFavorited
                        ? "text-orange-500 fill-orange-500"
                        : "text-white"
                    }`}
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      fillRule="evenodd"
                      d="M12 20c-2.205-.48-9-4.24-9-11a5 5 0 0 1 9-3a5 5 0 0 1 9 3c0 6.76-6.795 10.52-9 11"
                    />
                  </svg>
                </button>

                <Link
                  to={`/phim/${currentMovie.slug}`}
                  className="flex items-center justify-center text-white w-8 h-6 sm:w-11 sm:h-11 hover:text-orange-500 transition-colors duration-200"
                  aria-label={`Xem thông tin chi tiết phim ${currentMovie.ten_phim}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M12 2c5.523 0 10 4.477 10 10a10 10 0 0 1-19.995.324L2 12l.004-.28C2.152 6.327 6.57 2 12 2m.01 13l-.127.007a1 1 0 0 0 0 1.986L12 17l.127-.007a1 1 0 0 0 0-1.986zM12 7a1 1 0 0 0-.993.883L11 8v4l.007.117a1 1 0 0 0 1.986 0L13 12V8l-.007-.117A1 1 0 0 0 12 7"
                    />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </article>

          {/* Movie Thumbnails */}
          {movies.length > 0 && (
            <aside className="lg:col-span-5 flex justify-center lg:justify-end items-end mt-2 lg:mt-0">
              <nav
                className="flex flex-wrap justify-center gap-2 sm:gap-3 py-2 sm:py-3 px-2 bg-none rounded-xl"
                aria-label="Danh sách phim nổi bật"
                role="tablist"
              >
                {movies.map((movie, index) => (
                  <motion.button
                    key={`thumb-${movie.id || movie.slug}`}
                    onClick={() => handleThumbnailClick(movie, index)}
                    className={`w-6 h-6 rounded-full md:w-[4rem] md:h-12 object-cover lg:object-fill lg:rounded-lg cursor-pointer border-2 transition-all duration-300 ease-out
                      ${
                        currentMovie?.slug === movie.slug
                          ? "border-orange-500 scale-105 shadow-lg"
                          : "border-white/40 hover:border-white/50"
                      }`}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05 + 0.5,
                    }}
                    role="tab"
                    aria-selected={currentMovie?.slug === movie.slug}
                    aria-label={`Chọn phim ${movie.ten_phim}`}
                    type="button"
                  >
                    <img
                      src={movie.banner_url || "/placeholder-thumb.jpg"}
                      alt={`Thumbnail phim ${movie.ten_phim}`}
                      loading="lazy"
                      className="w-full h-full lg:object-fill rounded-full lg:rounded-md "
                    />
                  </motion.button>
                ))}
              </nav>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
